
import * as React from 'react';
import { useState } from 'react';

const serviceOptions = [
  'Aerial Crop Application',
  'Deer Deterrent Program',
  'Timber & Forestry Services',
  'Land Intelligence & Due Diligence',
  'Portfolio Monitoring',
  'Field Surveys & Mapping',
  'Recovery Services',
  'Infrastructure Inspection',
  'Multiple Services',
  'Not Sure — Need Guidance',
];

const acreageOptions = [
  'Under 200 acres',
  '200–500 acres',
  '500–1,000 acres',
  '1,000–2,500 acres',
  '2,500–5,000 acres',
  '5,000+ acres',
];

const timelineOptions = [
  'Urgent — within 1 week',
  'This month',
  'This season',
  'Planning for next season',
  'Just exploring options',
];

const hearAboutOptions = [
  'Referral from another farmer',
  'Google search',
  'Social media',
  'Industry event or trade show',
  'Local ag retailer',
  'Other',
];

export default function InquiryForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const details = formData.get('projectDetails') as string | null;
    if (details && details.length > 500) {
      // Exceeds max length – reset to idle and show no submission
      setStatus('idle');
      return;
    }

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d64vcni5k2n47qsg6ve0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          // URLSearchParams can accept FormData directly in modern browsers
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
        // Reset to idle after a short delay so the success UI is visible
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-10 flex flex-col items-center justify-center text-center min-h-[500px]">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-5">
          <i className="ri-check-line text-4xl text-emerald-700"></i>
        </div>
        <h3 className="text-2xl font-bold mb-3 tracking-wide">Inquiry Submitted!</h3>
        <p className="text-gray-600 max-w-md leading-relaxed">
          Thank you for reaching out. A member of our team will review your inquiry and get back to you within one business day.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
      <h3 className="text-2xl font-bold mb-1 tracking-wide">Detailed Inquiry Form</h3>
      <p className="text-sm text-gray-500 mb-6">
        Fields marked with <span className="text-red-600">*</span> are required
      </p>

      <form
        className="space-y-5"
        data-readdy-form
        id="contact-inquiry-form"
        onSubmit={handleSubmit}
      >
        {/* Personal Info */}
        <div className="pb-4 border-b border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">
            <a href="#your-information">Your Information</a>
          </h4>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="ci-firstName" className="block text-sm font-semibold mb-1.5">
                First Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="ci-firstName"
                name="firstName"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="John"
              />
            </div>
            <div>
              <label htmlFor="ci-lastName" className="block text-sm font-semibold mb-1.5">
                Last Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="ci-lastName"
                name="lastName"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="Smith"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="ci-email" className="block text-sm font-semibold mb-1.5">
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="ci-email"
                name="email"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label htmlFor="ci-phone" className="block text-sm font-semibold mb-1.5">
                Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="ci-phone"
                name="phone"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <div>
            <label htmlFor="ci-company" className="block text-sm font-semibold mb-1.5">
              Farm / Company Name
            </label>
            <input
              type="text"
              id="ci-company"
              name="companyName"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
              placeholder="Smith Family Farms"
            />
          </div>
        </div>

        {/* Operation Details */}
        <div className="pb-4 border-b border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">
            <a href="#operation-details">Operation Details</a>
          </h4>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="ci-service" className="block text-sm font-semibold mb-1.5">
                Service Interested In <span className="text-red-600">*</span>
              </label>
              <select
                id="ci-service"
                name="serviceType"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm bg-white cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>
                  Select a service
                </option>
                {serviceOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ci-acreage" className="block text-sm font-semibold mb-1.5">
                Total Acreage <span className="text-red-600">*</span>
              </label>
              <select
                id="ci-acreage"
                name="acreage"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm bg-white cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>
                  Select acreage range
                </option>
                {acreageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="ci-crops" className="block text-sm font-semibold mb-1.5">
                Crops / Land Type
              </label>
              <input
                type="text"
                id="ci-crops"
                name="crops"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="Corn, soybeans, wheat, timber..."
              />
            </div>
            <div>
              <label htmlFor="ci-location" className="block text-sm font-semibold mb-1.5">
                County / State <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="ci-location"
                name="location"
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm"
                placeholder="Lancaster County, PA"
              />
            </div>
          </div>
          <div>
            <label htmlFor="ci-timeline" className="block text-sm font-semibold mb-1.5">
              Timeline
            </label>
            <select
              id="ci-timeline"
              name="timeline"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm bg-white cursor-pointer"
              defaultValue=""
            >
              <option value="" disabled>
                When do you need service?
              </option>
              {timelineOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Project Details */}
        <div className="pb-4 border-b border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">
            <a href="#project-details">Tell Us More</a>
          </h4>
          <div className="mb-4">
            <label htmlFor="ci-details" className="block text-sm font-semibold mb-1.5">
              Describe Your Situation or Questions
            </label>
            <textarea
              id="ci-details"
              name="projectDetails"
              rows={5}
              maxLength={500}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent resize-none text-sm"
              placeholder="What challenges are you facing? What are you hoping to achieve? Any specific questions?"
              onChange={(e) => setCharCount(e.target.value.length)}
            ></textarea>
            <p className="text-xs text-gray-400 mt-1 text-right">{charCount}/500 characters</p>
          </div>
          <div>
            <label htmlFor="ci-hearAbout" className="block text-sm font-semibold mb-1.5">
              How Did You Hear About Us?
            </label>
            <select
              id="ci-hearAbout"
              name="hearAbout"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-transparent text-sm bg-white cursor-pointer"
              defaultValue=""
            >
              <option value="" disabled>
                Select an option
              </option>
              {hearAboutOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">
            <a href="#preferences">Preferences</a>
          </h4>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="preferPhone"
              value="Yes"
              className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">I prefer a phone call over email</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="urgentRequest"
              value="Yes"
              className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              This is an urgent request (narrow spray window, storm damage, etc.)
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="siteVisit"
              value="Yes"
              className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">I&apos;d like to schedule an on-site field visit</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="newsletter"
              value="Yes"
              className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Subscribe to seasonal updates and tips</span>
          </label>
        </div>

        {status === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-lg"></i>
            </div>
            Something went wrong. Please try again or call us directly.
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {status === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </span>
          ) : (
            'Submit Inquiry'
          )}
        </button>

        <p className="text-xs text-gray-400 text-center">
          By submitting, you agree to our{' '}
          <a href="#" rel="nofollow" className="underline hover:text-gray-600 cursor-pointer">
            Privacy Policy
          </a>
          . We&apos;ll never share your information.
        </p>
      </form>
    </div>
  );
}
